import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  MoreVertical, 
  Pause, 
  Play, 
  Eye, 
  Settings,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Twitter,
  MessageCircle,
  Instagram,
  Facebook,
  Youtube,
  Zap,
  RefreshCw,
  Clock,
  Timer,
  Server,
  Trash2,
  Shield,
  AlertCircle,
  Calendar,
  Edit,
  Save,
  Loader2
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ ADMIN CLEANUP PANEL (Admin Only) ============
const AdminCleanupPanel = ({ token, userRole }) => {
  const [loading, setLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fixSchedulesResult, setFixSchedulesResult] = useState(null);

  // Only show for admin users
  if (userRole !== 'admin') {
    return null;
  }

  const handleVerify = async () => {
    setLoading(true);
    setVerifyResult(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/cleanup/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setVerifyResult(data);
    } catch (error) {
      setVerifyResult({ error: error.message });
    }
    setLoading(false);
  };

  const handleCleanup = async () => {
    setLoading(true);
    setCleanupResult(null);
    setShowConfirm(false);
    try {
      const response = await fetch(`${API_URL}/api/admin/cleanup/production`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCleanupResult(data);
    } catch (error) {
      setCleanupResult({ error: error.message });
    }
    setLoading(false);
  };

  const handleFixSchedules = async () => {
    setLoading(true);
    setFixSchedulesResult(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/fix-missing-schedules`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setFixSchedulesResult(data);
      toast.success(data.message || 'Schedules fixed!');
    } catch (error) {
      setFixSchedulesResult({ error: error.message });
      toast.error('Failed to fix schedules');
    }
    setLoading(false);
  };

  return (
    <Card className="border-purple-700 mb-4">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-purple-400" />
          <CardTitle className="text-base">Admin: Database Cleanup</CardTitle>
          <Badge className="bg-purple-600">Admin Only</Badge>
        </div>
        <CardDescription className="text-gray-400">
          Remove duplicate schedules and clean up stale data
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={handleVerify}
            disabled={loading}
            className="border-blue-600 text-blue-400 hover:bg-blue-600/20"
          >
            {loading && !showConfirm ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Eye className="w-4 h-4 mr-2" />
            )}
            Check Status
          </Button>
          
          {!showConfirm ? (
            <Button 
              variant="outline"
              onClick={() => setShowConfirm(true)}
              disabled={loading}
              className="border-red-600 text-red-400 hover:bg-red-600/20"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Run Cleanup
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-yellow-400 text-sm">Are you sure?</span>
              <Button 
                variant="destructive"
                onClick={handleCleanup}
                disabled={loading}
                size="sm"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-1" />
                )}
                Yes, Clean Up
              </Button>
              <Button 
                variant="ghost"
                onClick={() => setShowConfirm(false)}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          )}
          
          <Button 
            variant="outline"
            onClick={handleFixSchedules}
            disabled={loading}
            className="border-blue-600 text-blue-400 hover:bg-blue-600/20"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Calendar className="w-4 h-4 mr-2" />
            )}
            Fix Missing Schedules
          </Button>
        </div>

        {/* Fix Schedules Result */}
        {fixSchedulesResult && !fixSchedulesResult.error && (
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-blue-400 font-medium">
              <CheckCircle className="w-4 h-4" />
              {fixSchedulesResult.message}
            </div>
            {fixSchedulesResult.fixed_channels?.length > 0 && (
              <div className="text-sm text-gray-300">
                {fixSchedulesResult.fixed_channels.map((ch, i) => (
                  <div key={i} className="ml-4">
                    • {ch.name}: {ch.engines_fixed?.map(e => `${e.engine} (${e.times.join(', ')})`).join(', ')}
                  </div>
                ))}
              </div>
            )}
            {fixSchedulesResult.scheduler_refreshed && (
              <div className="text-xs text-green-400 mt-2">✓ Scheduler refreshed</div>
            )}
          </div>
        )}

        {/* Verify Result */}
        {verifyResult && !verifyResult.error && (
          <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-300">Current Status</span>
              <Badge className={verifyResult.stats?.duplicate_schedule_groups > 0 ? 'bg-red-600' : 'bg-green-600'}>
                {verifyResult.stats?.duplicate_schedule_groups > 0 ? 'Issues Found' : 'All Clean'}
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Total Channels:</span>
                <span className="ml-2 text-white">{verifyResult.stats?.total_channels}</span>
              </div>
              <div>
                <span className="text-gray-400">Total Schedules:</span>
                <span className="ml-2 text-white">{verifyResult.stats?.total_schedules}</span>
              </div>
              <div>
                <span className="text-gray-400">Enabled:</span>
                <span className="ml-2 text-green-400">{verifyResult.stats?.enabled_schedules}</span>
              </div>
              <div>
                <span className="text-gray-400">Duplicates:</span>
                <span className={`ml-2 ${verifyResult.stats?.duplicate_schedule_groups > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {verifyResult.stats?.duplicate_schedule_groups}
                </span>
              </div>
            </div>

            {verifyResult.issues?.length > 0 && (
              <div className="mt-2 p-2 bg-red-900/30 rounded text-sm">
                <div className="text-red-400 font-medium mb-1">Issues:</div>
                {verifyResult.issues.map((issue, idx) => (
                  <div key={idx} className="text-red-300">
                    {issue.type}: {JSON.stringify(issue.details).slice(0, 100)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cleanup Result */}
        {cleanupResult && !cleanupResult.error && (
          <div className="bg-green-900/30 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="font-medium text-green-300">Cleanup Complete!</span>
            </div>
            <div className="text-sm space-y-1 text-gray-300">
              <div>✓ Duplicates removed: <span className="text-white">{cleanupResult.summary?.duplicates_removed || 0}</span></div>
              <div>✓ Orphans removed: <span className="text-white">{cleanupResult.summary?.orphans_removed || 0}</span></div>
              <div>✓ Stale logs cleared: <span className="text-white">{cleanupResult.summary?.stale_logs_cleared || 0}</span></div>
              <div>✓ Final schedules: <span className="text-white">{cleanupResult.summary?.final_schedule_count || 0}</span></div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {(verifyResult?.error || cleanupResult?.error) && (
          <div className="bg-red-900/30 rounded-lg p-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300">{verifyResult?.error || cleanupResult?.error}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============ SCHEDULER STATUS PANEL ============
const SchedulerStatusPanel = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchStatus = React.useCallback(async () => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/api/admin/scheduler-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setStatus(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchStatus();
      // Refresh every 30 seconds
      const interval = setInterval(fetchStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [fetchStatus, token]);

  // Show error state
  if (error) {
    return (
      <Card className="border-red-700 mb-4">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-red-400" />
            <CardTitle className="text-base">Scheduler Status</CardTitle>
            <Badge className="bg-red-600">Error</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-400">Failed to load: {error}</p>
          <Button variant="ghost" size="sm" onClick={fetchStatus} className="mt-2">
            <RefreshCw className="w-3 h-3 mr-1" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <Card className="border-gray-700 mb-4">
        <CardContent className="py-4 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm text-gray-400">Loading scheduler status...</span>
        </CardContent>
      </Card>
    );
  }

  // Show empty state if no data
  if (!status) {
    return (
      <Card className="border-gray-700 mb-4">
        <CardContent className="py-4">
          <span className="text-sm text-gray-400">No scheduler data available</span>
        </CardContent>
      </Card>
    );
  }

  // State: 'stopped', 'running', 'paused' (string values from persistent scheduler)
  // Also support legacy numeric values: 0=stopped, 1=running, 2=paused
  const schedulerState = status.scheduler?.state;
  const schedulerRunning = status.scheduler?.running;
  const isRunning = schedulerRunning === true || schedulerState === 'running' || schedulerState === 1 || schedulerState === '1';
  const isPaused = schedulerState === 'paused' || schedulerState === 2 || schedulerState === '2';
  const isStopped = schedulerState === 'stopped' || schedulerState === 0 || schedulerState === '0' || (!isRunning && !isPaused);
  
  // Support both old metrics format and new persistent scheduler format
  const metrics = status.metrics || {};
  const last24h = status.last_24h || {};
  const today = status.today || {};
  
  // Get stats from either format
  const jobsSucceeded = last24h.by_status?.completed || metrics.jobs_succeeded || 0;
  const jobsFailed = last24h.by_status?.missed || metrics.jobs_failed || 0;
  const jobsSkipped = metrics.jobs_skipped || 0;
  const successRate = last24h.success_rate || metrics.success_rate;
  const lastTickAt = metrics.last_tick_at;
  const currentTimeIst = status.current_time_ist || today.time_ist;

  return (
    <Card className={`border ${isRunning ? 'border-green-700' : isPaused ? 'border-orange-700' : 'border-yellow-700'} mb-4`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Server className={`w-5 h-5 ${isRunning ? 'text-green-400' : isPaused ? 'text-orange-400' : 'text-yellow-400'}`} />
            <CardTitle className="text-base">Scheduler Status</CardTitle>
            <Badge className={isRunning ? 'bg-green-600' : isPaused ? 'bg-orange-600' : 'bg-yellow-600'}>
              {isRunning ? 'Running' : isPaused ? 'Paused' : isStopped ? 'Stopped' : 'Unknown'}
            </Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Quick Stats Row */}
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-green-400">{jobsSucceeded}</div>
            <div className="text-xs text-gray-400">Succeeded</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-400">{jobsFailed}</div>
            <div className="text-xs text-gray-400">Failed</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-yellow-400">{jobsSkipped}</div>
            <div className="text-xs text-gray-400">Skipped</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-400">{last24h.executions || today.posts_published || 0}</div>
            <div className="text-xs text-gray-400">Today</div>
          </div>
        </div>

        {/* Last Tick / Activity */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-gray-400">
            <Clock className="w-4 h-4" />
            <span>Last tick:</span>
            <span className={lastTickAt || isRunning ? 'text-green-400' : 'text-gray-500'}>
              {lastTickAt 
                ? new Date(lastTickAt).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })
                : isRunning ? 'Active' : 'Never'
              }
            </span>
          </div>
          <div className="flex items-center gap-2">
            {today.quiet_hours_active && (
              <Badge variant="outline" className="text-yellow-400 border-yellow-400">
                🌙 Quiet Hours Active
              </Badge>
            )}
            <span className="text-xs text-gray-500">{currentTimeIst}</span>
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="space-y-3 pt-2 border-t border-gray-700">
            {/* Success Rate */}
            {successRate !== null && successRate !== undefined && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Success Rate</span>
                <span className={successRate >= 90 ? 'text-green-400' : successRate >= 70 ? 'text-yellow-400' : 'text-red-400'}>
                  {successRate}%
                </span>
              </div>
            )}

            {/* Recent Executions (from persistent scheduler) */}
            {status.recent_executions?.length > 0 && (
              <div className="bg-gray-800/50 rounded p-2 text-xs">
                <div className="text-gray-400 mb-1">Recent Executions:</div>
                {status.recent_executions.slice(0, 3).map((exec, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1 border-b border-gray-700 last:border-0">
                    <span>{exec.engine_id}</span>
                    <Badge variant={exec.result_status === 'published' ? 'default' : 'secondary'} className="text-xs">
                      {exec.result_status || exec.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}

            {/* Legacy: Last Job (fallback for old format) */}
            {!status.recent_executions?.length && metrics.last_job?.at && (
              <div className="bg-gray-800/50 rounded p-2 text-xs">
                <div className="text-gray-400 mb-1">Last Job:</div>
                <div className="flex items-center justify-between">
                  <span>{metrics.last_job.engine_id}</span>
                  <Badge variant={metrics.last_job.status === 'published' ? 'default' : 'secondary'} className="text-xs">
                    {metrics.last_job.status}
                  </Badge>
                </div>
                <div className="text-gray-500 mt-1">
                  {new Date(metrics.last_job.at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                </div>
              </div>
            )}

            {/* Schedules Info */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Active Schedules</span>
              <span>{status.schedules?.enabled || 0} / {status.schedules?.total || 0}</span>
            </div>

            {/* Upcoming Jobs */}
            {status.upcoming_jobs?.length > 0 && (
              <div>
                <div className="text-xs text-gray-400 mb-1">Upcoming Jobs:</div>
                <div className="space-y-1">
                  {status.upcoming_jobs.slice(0, 5).map((job, idx) => {
                    // Handle special system jobs
                    const isMatchMonitor = job.job_id === 'match_monitor' || job.platform === 'system';
                    const displayName = isMatchMonitor ? 'Match Monitor' : (job.channel_name || 'Unknown');
                    const displayEngine = isMatchMonitor ? 'cricket updates' : (job.engine_id?.replace('_', ' ') || 'unknown');
                    
                    // Platform badge
                    const getPlatformBadge = () => {
                      if (isMatchMonitor) return { bg: 'bg-green-500/20', text: 'text-green-300', label: '🏏' };
                      if (job.platform === 'telegram') return { bg: 'bg-blue-500/20', text: 'text-blue-300', label: 'TG' };
                      if (job.platform === 'twitter') return { bg: 'bg-sky-500/20', text: 'text-sky-300', label: 'X' };
                      if (job.platform === 'facebook') return { bg: 'bg-blue-600/20', text: 'text-blue-400', label: 'FB' };
                      if (job.platform === 'instagram') return { bg: 'bg-pink-500/20', text: 'text-pink-300', label: 'IG' };
                      if (job.platform === 'discord_webhook') return { bg: 'bg-indigo-500/20', text: 'text-indigo-300', label: 'DC' };
                      if (job.platform === 'discord_bot') return { bg: 'bg-indigo-600/20', text: 'text-indigo-400', label: 'DC' };
                      return { bg: 'bg-gray-500/20', text: 'text-gray-300', label: '?' };
                    };
                    const badge = getPlatformBadge();
                    
                    return (
                      <div key={idx} className="flex items-center justify-between text-xs bg-gray-800/30 rounded px-2 py-1">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${badge.bg} ${badge.text}`}>
                            {badge.label}
                          </span>
                          <span className="text-gray-300">
                            {displayName} • {displayEngine}
                          </span>
                        </div>
                        <span className="text-blue-400">
                          {new Date(job.next_run).toLocaleTimeString('en-IN', { 
                            timeZone: 'Asia/Kolkata',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recent Errors */}
            {metrics.recent_errors?.length > 0 && (
              <div>
                <div className="text-xs text-red-400 mb-1">Recent Errors:</div>
                <div className="space-y-1">
                  {metrics.recent_errors.slice(0, 2).map((err, idx) => (
                    <div key={idx} className="text-xs bg-red-900/20 rounded px-2 py-1 text-red-300">
                      {err.engine_id}: {err.error.slice(0, 50)}...
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Config Info */}
            <div className="text-xs text-gray-500">
              Quiet Hours: {status.config?.quiet_hours_start} - {status.config?.quiet_hours_end} IST
            </div>

            {/* Admin Controls */}
            <div className="pt-2 border-t border-gray-700">
              <div className="text-xs text-gray-400 mb-2">Admin Controls:</div>
              <div className="flex gap-2">
                <SchedulerToggleButton 
                  isRunning={isRunning}
                  isPaused={isPaused}
                  token={token} 
                  onToggle={fetchStatus}
                />
              </div>
            </div>
          </div>
        )}

        {/* Refresh Button */}
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={fetchStatus}>
            <RefreshCw className="w-3 h-3 mr-1" />
            Refresh
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Scheduler Toggle Button Component
const SchedulerToggleButton = ({ isRunning, isPaused, token, onToggle }) => {
  const [loading, setLoading] = useState(false);

  const handleToggle = async () => {
    setLoading(true);
    try {
      // If paused, resume. If running, disable. If stopped, enable.
      let endpoint;
      if (isPaused) {
        endpoint = '/api/admin/scheduler/resume';
      } else if (isRunning) {
        endpoint = '/api/admin/scheduler/disable';
      } else {
        endpoint = '/api/admin/scheduler/enable';
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (response.ok) {
        onToggle();
      } else {
        alert(data.detail || 'Failed to toggle scheduler');
      }
    } catch (error) {
      alert('Error toggling scheduler');
    }
    setLoading(false);
  };

  // Determine button appearance based on state
  let buttonText, ButtonIcon, variant;
  if (isPaused) {
    buttonText = 'Resume Scheduler';
    ButtonIcon = Play;
    variant = 'default';
  } else if (isRunning) {
    buttonText = 'Pause Scheduler';
    ButtonIcon = Pause;
    variant = 'destructive';
  } else {
    buttonText = 'Enable Scheduler';
    ButtonIcon = Play;
    variant = 'default';
  }

  return (
    <Button
      size="sm"
      variant={variant}
      onClick={handleToggle}
      disabled={loading}
      className="text-xs"
    >
      {loading ? (
        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
      ) : (
        <ButtonIcon className="w-3 h-3 mr-1" />
      )}
      {buttonText}
    </Button>
  );
};

// Platform icons
const PlatformIcon = ({ platform, className = "w-4 h-4" }) => {
  const icons = {
    twitter: <Twitter className={className} />,
    telegram: <MessageCircle className={className} />,
    instagram: <Instagram className={className} />,
    facebook: <Facebook className={className} />,
    youtube: <Youtube className={className} />
  };
  return icons[platform] || <Activity className={className} />;
};

// Health status badge
const HealthBadge = ({ status, failures, needsAttention, autoPausedAt }) => {
  const config = {
    healthy: { color: 'bg-green-500', icon: CheckCircle, label: 'Healthy' },
    warning: { color: 'bg-yellow-500', icon: AlertTriangle, label: `Warning (${failures} fails)` },
    error: { color: 'bg-red-500', icon: XCircle, label: `Error (${failures} fails)` },
    needs_attention: { color: 'bg-orange-500 animate-pulse', icon: AlertTriangle, label: '⚠️ Needs Attention' }
  };
  const { color, icon: Icon, label } = config[status] || config.healthy;
  
  return (
    <div className="flex flex-col items-end">
      <Badge className={`${color} text-white text-xs flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
      {needsAttention && autoPausedAt && (
        <span className="text-xs text-orange-400 mt-1">
          Auto-paused {new Date(autoPausedAt).toLocaleDateString()}
        </span>
      )}
    </div>
  );
};

// Engine badge
const EngineBadge = ({ engineId }) => {
  const engineNames = {
    crypto_movers: '🪙 Crypto',
    crypto_signals: '📊 Signals',
    sports_lifecycle: '⚽ Sports',
    casino_trivia: '🎰 Casino',
    casino_news: '🗞️ Casino News',
    how_to_play: '📚 How-to',
    meme_engagement: '😂 Memes',
    promotions: '📢 Promo',
    announcements: '📣 News',
    bollywood_news: '🎬 Bollywood',
    daily_tips: '💡 Tips',
    crypto_fear_greed: '📊 Fear & Greed',
    daily_tips: '💡 Tips',
    polls_quizzes: '📊 Polls',
    weekly_recap: '📋 Recap',
    joke_of_the_day: '🃏 Jokes',
    this_day_in_history: '📅 History',
    daily_horoscope: '🔮 Horoscope',
    weather_updates: '🌤️ Weather',
    random_facts: '💡 Facts',
    riddles: '🧩 Riddles',
    movie_trivia: '🎬 Movies',
    gold_prices: '💰 Gold',
    festival_countdown: '🎉 Countdown',
    tech_news: '📱 Tech',
    player_stats: '📊 Players',
    slot_reviews: '🎲 Slots'
  };
  return (
    <Badge variant="outline" className="text-xs">
      {engineNames[engineId] || engineId}
    </Badge>
  );
};

// Instagram Cross-Post Toggle Component
const InstagramCrossPostToggle = ({ channelId, enabled, onToggle }) => {
  const [loading, setLoading] = useState(false);
  const [isEnabled, setIsEnabled] = useState(enabled || false);
  
  // Sync state when props change (e.g., after page refresh)
  useEffect(() => {
    setIsEnabled(enabled || false);
  }, [enabled]);
  
  const handleToggle = async (e) => {
    e.stopPropagation(); // Prevent any parent handlers
    if (loading) return;
    
    setLoading(true);
    const token = localStorage.getItem('token');
    const newState = !isEnabled;
    
    try {
      const response = await fetch(`${API_URL}/api/channels/${channelId}/cross-post-instagram?enabled=${newState}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Store status before reading body (reading body doesn't affect .ok)
      const isOk = response.ok;
      const status = response.status;
      
      // Read body once
      let result = {};
      try {
        result = await response.json();
      } catch {
        result = {};
      }
      
      if (isOk) {
        setIsEnabled(newState);
        toast.success(result.message || 'Cross-post setting updated');
        // Don't auto-refresh - keep the visual state
        // The setting is saved in the database
      } else {
        toast.error(result.detail || `Failed to toggle cross-post (${status})`);
      }
    } catch (error) {
      toast.error(error.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div 
      className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-all ${
        isEnabled 
          ? 'bg-pink-900/30 border-pink-500/50 hover:border-pink-400' 
          : 'bg-gray-800/50 border-gray-600 hover:border-gray-500'
      }`}
      onClick={handleToggle}
    >
      <div className="flex items-center gap-2">
        <Instagram className={`w-4 h-4 ${isEnabled ? 'text-pink-400' : 'text-gray-500'}`} />
        <span className={`text-sm ${isEnabled ? 'text-pink-300' : 'text-gray-400'}`}>
          Cross-post to Instagram
        </span>
      </div>
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
      ) : (
        <div className={`w-8 h-4 rounded-full transition-colors ${isEnabled ? 'bg-pink-500' : 'bg-gray-600'}`}>
          <div className={`w-3 h-3 rounded-full bg-white transition-transform mt-0.5 ${isEnabled ? 'ml-4' : 'ml-0.5'}`} />
        </div>
      )}
    </div>
  );
};

// Channel Card Component
const ChannelCard = ({ channel, onPause, onResume, onRefresh, brands, connectedAccounts }) => {
  const [testing, setTesting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedEngine, setSelectedEngine] = useState(null);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    display_name: channel.display_name || '',
    brand_id: channel.brand_id || '',
    channel_identifier: channel.channel_identifier || '',
    connected_account_id: channel.account_ids?.[0] || ''
  });

  const handleEditChannel = async () => {
    setEditLoading(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        display_name: editForm.display_name,
        brand_id: editForm.brand_id
      };
      
      const response = await fetch(`${API_URL}/api/channels/${channel.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update channel');
      }
      
      toast.success('Channel updated successfully!');
      setShowEditModal(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.message || 'Failed to update channel');
    }
    setEditLoading(false);
  };

  const handlePreviewEngine = async (engineId) => {
    setTesting(true);
    setSelectedEngine(engineId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/engines/${engineId}/preview/${channel.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPreviewContent(data);
      setShowPreview(true);
    } catch (error) {
    }
    setTesting(false);
  };

  const handlePublish = async () => {
    if (!selectedEngine || !previewContent) return;
    
    setPublishing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/engines/${selectedEngine}/publish/${channel.id}?force=true`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Clone response before reading to avoid "body stream already read" error
      const responseClone = response.clone();
      
      if (!response.ok) {
        // Try to get error message from response
        try {
          const errorData = await responseClone.json();
          alert(`❌ Failed: ${errorData.detail || errorData.error || errorData.message || `HTTP ${response.status}`}`);
        } catch {
          alert(`❌ Failed: HTTP ${response.status}`);
        }
        setPublishing(false);
        return;
      }
      
      const data = await response.json();
      
      if (data.status === 'published') {
        alert('✅ Published successfully!');
        setShowPreview(false);
        onRefresh && onRefresh();
      } else {
        alert(`❌ Failed: ${data.error || data.message || 'Unknown error'}`);
      }
    } catch (error) {
      alert('❌ Publish failed: ' + error.message);
    }
    setPublishing(false);
  };

  // Format time ago
  const timeAgo = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <Card className={`border ${channel.status === 'paused' ? 'opacity-60 border-gray-600' : 'border-gray-700'}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${channel.platform === 'twitter' ? 'bg-blue-900' : 'bg-purple-900'}`}>
              <PlatformIcon platform={channel.platform} className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-base">{channel.display_name}</CardTitle>
              <CardDescription className="text-xs">@{channel.handle}</CardDescription>
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {channel.status === 'active' ? (
                <DropdownMenuItem onClick={() => onPause(channel.id)}>
                  <Pause className="w-4 h-4 mr-2" /> Pause Channel
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onResume(channel.id)}>
                  <Play className="w-4 h-4 mr-2" /> Resume Channel
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => {
                setEditForm({
                  display_name: channel.display_name || '',
                  brand_id: channel.brand_id || '',
                  channel_identifier: channel.channel_identifier || '',
                  connected_account_id: channel.account_ids?.[0] || ''
                });
                setShowEditModal(true);
              }}>
                <Edit className="w-4 h-4 mr-2" /> Edit Channel
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-red-500 focus:text-red-500"
                onClick={async () => {
                  if (window.confirm(`Are you sure you want to delete "${channel.display_name}"? This action cannot be undone.`)) {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await fetch(`${API_URL}/api/channels/${channel.id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (response.ok) {
                        toast.success(`Channel "${channel.display_name}" deleted`);
                        if (onRefresh) onRefresh();
                      } else {
                        const error = await response.json();
                        toast.error(error.detail || 'Failed to delete channel');
                      }
                    } catch (error) {
                      toast.error('Failed to delete channel');
                    }
                  }
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete Channel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Edit Channel Modal */}
          <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Edit className="w-5 h-5" />
                  Edit Channel
                </DialogTitle>
                <DialogDescription>
                  Update channel settings. Changes are saved immediately.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Display Name */}
                <div className="space-y-2">
                  <Label htmlFor="display_name">Display Name</Label>
                  <Input
                    id="display_name"
                    value={editForm.display_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, display_name: e.target.value }))}
                    placeholder="Channel display name"
                  />
                </div>
                
                {/* Brand Selection */}
                <div className="space-y-2">
                  <Label>Brand</Label>
                  <Select 
                    value={editForm.brand_id} 
                    onValueChange={(value) => setEditForm(prev => ({ ...prev, brand_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                    <SelectContent>
                      {brands?.map(brand => (
                        <SelectItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Moving to a different brand affects duplicate content detection.
                  </p>
                </div>
                
                {/* Channel Info (Read-only) */}
                <div className="space-y-2">
                  <Label>Platform</Label>
                  <Input value={channel.platform} disabled className="bg-gray-800" />
                </div>
                
                <div className="space-y-2">
                  <Label>Channel Handle</Label>
                  <Input 
                    value={channel.channel_identifier || 'Not set'} 
                    disabled 
                    className="bg-gray-800"
                  />
                  <p className="text-xs text-gray-500">
                    To change the Chat ID/handle, delete and recreate the channel.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleEditChannel} disabled={editLoading}>
                  {editLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Status row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant={channel.status === 'active' ? 'default' : 'secondary'}>
              {channel.status}
            </Badge>
            <Badge variant="outline" className="text-xs capitalize">
              {channel.purpose}
            </Badge>
          </div>
          <HealthBadge 
            status={channel.health_status} 
            failures={channel.consecutive_failures}
            needsAttention={channel.needs_attention}
            autoPausedAt={channel.auto_paused_at}
          />
        </div>
        
        {/* Autopilot status */}
        <div className="flex items-center gap-2 text-sm">
          <Zap className={`w-4 h-4 ${channel.autopilot_enabled ? 'text-yellow-400' : 'text-gray-500'}`} />
          <span className={channel.autopilot_enabled ? 'text-yellow-400' : 'text-gray-500'}>
            {channel.autopilot_enabled ? 'Autopilot ON' : 'Manual Only'}
          </span>
        </div>
        
        {/* Instagram Cross-Post Toggle (Facebook channels only) */}
        {channel.platform === 'facebook' && (
          <InstagramCrossPostToggle 
            channelId={channel.id}
            enabled={channel.cross_post_to_instagram}
            onToggle={onRefresh}
          />
        )}
        
        {/* Attached engines */}
        {channel.attached_engines?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {channel.attached_engines.map(engineId => (
              <EngineBadge key={engineId} engineId={engineId} />
            ))}
          </div>
        )}
        
        {/* Last post info */}
        <div className="text-xs text-gray-400 space-y-1">
          <div className="flex items-center gap-1">
            <span>Last post:</span>
            <span className={channel.last_post_at ? 'text-green-400' : ''}>
              {timeAgo(channel.last_post_at)}
            </span>
          </div>
          {channel.next_scheduled_at && (
            <div className="flex items-center gap-1">
              <span>Next scheduled:</span>
              <span className="text-blue-400">
                {new Date(channel.next_scheduled_at).toLocaleString('en-IN', {
                  timeZone: 'Asia/Kolkata',
                  hour: '2-digit',
                  minute: '2-digit',
                  month: 'short',
                  day: 'numeric'
                })} IST
              </span>
            </div>
          )}
        </div>
        
        {/* Needs Attention Alert */}
        {channel.needs_attention && (
          <div className="bg-orange-900/30 border border-orange-500 rounded-lg p-2 text-xs text-orange-300">
            ⚠️ This channel was auto-paused after {channel.consecutive_failures} consecutive failures. 
            Manual resume required.
          </div>
        )}
        
        {/* Quick actions */}
        <div className="flex gap-2 pt-2">
          {channel.attached_engines?.length > 0 && (
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
              <DialogTrigger asChild>
                <Button 
                  size="sm" 
                  variant="outline" 
                  disabled={testing}
                  onClick={() => handlePreviewEngine(channel.attached_engines[0])}
                >
                  <Eye className="w-3 h-3 mr-1" />
                  {testing ? 'Loading...' : 'Preview'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Content Preview</DialogTitle>
                  <DialogDescription>
                    Preview of what would be posted to {previewContent?.channel}
                  </DialogDescription>
                </DialogHeader>
                {previewContent && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-gray-400">Engine</label>
                      <p className="font-medium">{previewContent.engine_id || previewContent.engine}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Platform</label>
                      <p className="font-medium capitalize">{previewContent.platform}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Content</label>
                      <div className="bg-gray-800 p-3 rounded-lg mt-1 whitespace-pre-wrap text-white">
                        {previewContent.processed_content || previewContent.content || previewContent.preview_content || 'No content generated'}
                      </div>
                    </div>
                    {previewContent.error && (
                      <div className="text-red-400 text-sm">
                        Error: {previewContent.error}
                      </div>
                    )}
                    <div className="flex gap-2 pt-4">
                      <Button 
                        onClick={handlePublish} 
                        disabled={publishing || previewContent.status === 'error'}
                        className="flex-1"
                      >
                        {publishing ? 'Publishing...' : '🚀 Publish Now'}
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setShowPreview(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Brand Section Component
const BrandSection = ({ brand, channels, onPauseChannel, onResumeChannel, onPauseBrand, onResumeBrand, onRefresh, allBrands }) => {
  const activeCount = channels?.filter(c => c.status === 'active').length || 0;
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-xl font-bold">{brand.name}</h3>
          <Badge variant="outline">{channels?.length || 0} channels</Badge>
          <Badge variant={activeCount > 0 ? 'default' : 'secondary'}>
            {activeCount} active
          </Badge>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <MoreVertical className="w-4 h-4 mr-1" />
              Brand Actions
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {brand.status === 'active' ? (
              <DropdownMenuItem onClick={() => onPauseBrand(brand.id)}>
                <Pause className="w-4 h-4 mr-2" /> Pause All Channels
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => onResumeBrand(brand.id)}>
                <Play className="w-4 h-4 mr-2" /> Resume All Channels
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {channels?.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {channels.map(channel => (
            <ChannelCard
              key={channel.id}
              channel={channel}
              onPause={onPauseChannel}
              onResume={onResumeChannel}
              onRefresh={onRefresh}
              brands={allBrands}
            />
          ))}
        </div>
      ) : (
        <Card className="border-dashed border-gray-600">
          <CardContent className="py-8 text-center text-gray-400">
            No channels configured for this brand yet.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Main Control Room Component
export default function ControlRoom() {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/control-room`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      setData(result);
    } catch (error) {
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePauseChannel = async (channelId) => {
    try {
      await fetch(`${API_URL}/api/channels/${channelId}/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
    }
  };

  const handleResumeChannel = async (channelId) => {
    try {
      await fetch(`${API_URL}/api/channels/${channelId}/resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
    }
  };

  const handlePauseBrand = async (brandId) => {
    try {
      await fetch(`${API_URL}/api/brands/${brandId}/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
    }
  };

  const handleResumeBrand = async (brandId) => {
    try {
      await fetch(`${API_URL}/api/brands/${brandId}/resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8 text-gray-400">
        Failed to load control room data.
      </div>
    );
  }

  // Filter brands with channels for tabs
  const brandsWithChannels = data.brands.filter(b => 
    data.channels_by_brand[b.id]?.length > 0
  );

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Channels</CardDescription>
            <CardTitle className="text-3xl">{data.total_channels}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active</CardDescription>
            <CardTitle className="text-3xl text-green-400">{data.active_channels}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Paused</CardDescription>
            <CardTitle className="text-3xl text-yellow-400">{data.paused_channels}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Healthy</CardDescription>
            <CardTitle className="text-3xl text-green-400">{data.healthy_channels}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Issues</CardDescription>
            <CardTitle className="text-3xl text-red-400">
              {data.warning_channels + data.error_channels}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Scheduler Status Panel */}
      <SchedulerStatusPanel token={token} />

      {/* Admin Cleanup Panel (Only visible to admin) */}
      <AdminCleanupPanel token={token} userRole={user?.role} />

      {/* Refresh button */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Brand Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Brands</TabsTrigger>
          {brandsWithChannels.map(brand => (
            <TabsTrigger key={brand.id} value={brand.id}>
              {brand.name} ({data.channels_by_brand[brand.id]?.length || 0})
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="all" className="space-y-8 mt-6">
          {data.brands.map(brand => (
            <BrandSection
              key={brand.id}
              brand={brand}
              channels={data.channels_by_brand[brand.id]}
              onPauseChannel={handlePauseChannel}
              onResumeChannel={handleResumeChannel}
              onPauseBrand={handlePauseBrand}
              onResumeBrand={handleResumeBrand}
              onRefresh={fetchData}
              allBrands={data.brands}
            />
          ))}
        </TabsContent>

        {brandsWithChannels.map(brand => (
          <TabsContent key={brand.id} value={brand.id} className="mt-6">
            <BrandSection
              brand={brand}
              channels={data.channels_by_brand[brand.id]}
              onPauseChannel={handlePauseChannel}
              onResumeChannel={handleResumeChannel}
              onPauseBrand={handlePauseBrand}
              onResumeBrand={handleResumeBrand}
              onRefresh={fetchData}
              allBrands={data.brands}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
